/**
 * The Captains screen (M6 spec §5, §8; plan P32): the hired captains with portrait, state, assignment, wage,
 * loyalty and XP, with reassign (one captain per ship) and fire, and the memorial on its own tab. It renders a
 * `CaptainsScreenState` and dispatches `CaptainsAction`s; every rule is in `models/captains.ts`. The focused
 * control carries `data-focused` (keyboard and pad focus are the model's, not the DOM's). UI events only.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { BAR, summaryLine, type BarControl, type CaptainRow, type CaptainsAction, type CaptainsConfig, type CaptainsScreenState, type RowColumn } from './models/captains'
import { Memorial } from './Memorial'
import { Portrait } from './Portrait'
import '../strategic/strategic.css'
import './fleet.css'
import './captains.css'

export interface CaptainsProps {
  state: CaptainsScreenState
  config: CaptainsConfig
  dispatch: (a: CaptainsAction) => void
  loading?: boolean
}

const BAR_LABEL: Readonly<Record<BarControl, string>> = Object.freeze({
  'view:roster': 'Captains',
  'view:memorial': 'Memorial',
})

function focusAttr(on: boolean): { 'data-focused'?: '' } {
  return on ? { 'data-focused': '' } : {}
}

function rowFocus(s: CaptainsScreenState, i: number, col: RowColumn): boolean {
  return s.picker === null && s.focus.zone === 'list' && s.focus.index === i && s.focus.col === col
}

function CaptainItem(p: { s: CaptainsScreenState; r: CaptainRow; i: number; dispatch: (a: CaptainsAction) => void }): ReactNode {
  const { s, r, i } = p
  const armed = s.confirmFire === r.id
  const picker = s.picker !== null && s.picker.captainId === r.id ? s.picker : null
  return (
    <li className="cp-row" data-testid="captain-row" data-captain={r.id} data-state={r.state}>
      <Portrait recipe={r.portrait} />
      <div className="cp-row__who">
        <div className="cp-row__name">{r.name}</div>
        <div className="cp-row__sub" data-testid="captain-state">
          {r.stateLabel} · {r.assignment}
        </div>
        <div className="cp-row__sub">{r.skills}</div>
        <div className="cp-row__sub">{r.traits}</div>
      </div>
      <div className="cp-row__stats">
        <div className="ui-num" data-testid="captain-wage">
          {r.wageLabel}
        </div>
        <div className="cp-loyalty" data-low={r.loyaltyLow ? '' : undefined} title="Loyalty">
          <span className="cp-loyalty__bar" aria-hidden="true">
            <span className="cp-loyalty__fill" style={{ width: `${r.loyalty}%` }} />
          </span>
          <span data-testid="captain-loyalty">Loyalty {r.loyalty}</span>
        </div>
        <div className="cp-row__sub" data-testid="captain-xp">
          XP {r.xpTotal} · {r.xpLabel}
        </div>
      </div>
      <div className="cp-row__actions">
        <button
          type="button"
          tabIndex={-1}
          className="sui-btn"
          data-testid="captain-reassign"
          aria-disabled={!r.canReassign}
          title={r.reassignReason === '' ? undefined : r.reassignReason}
          {...focusAttr(rowFocus(s, i, 'assign') || picker !== null)}
          onClick={() => p.dispatch({ type: 'pressRow', index: i, col: 'assign' })}
        >
          Reassign
        </button>
        <button
          type="button"
          tabIndex={-1}
          className="sui-btn cp-fire"
          data-testid="captain-fire"
          data-armed={armed ? '' : undefined}
          aria-disabled={!r.canFire}
          {...focusAttr(rowFocus(s, i, 'fire'))}
          onClick={() => p.dispatch({ type: 'pressRow', index: i, col: 'fire' })}
        >
          {armed ? `Confirm: fire ${r.name}` : 'Fire'}
        </button>
      </div>
      {picker === null ? null : (
        <ul className="cp-picker" role="listbox" aria-label={`Reassign ${r.name}`} data-testid="captain-picker">
          {picker.options.map((o, k) => (
            <li key={o.shipId ?? 'none'} role="option" aria-selected={k === picker.index}>
              <button type="button" tabIndex={-1} className="sui-btn" data-testid="captain-pick" {...focusAttr(k === picker.index)} onClick={() => p.dispatch({ type: 'pick', index: k })}>
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

export function Captains(p: CaptainsProps): ReactNode {
  const s = p.state
  const listRef = useRef<HTMLDivElement>(null)
  const focusKey = s.focus.zone === 'list' ? `${s.tab}:${s.focus.index}:${s.picker?.index ?? -1}` : ''
  useEffect(() => {
    if (focusKey === '') return
    listRef.current?.querySelector('[data-focused]')?.scrollIntoView({ block: 'nearest' })
  }, [focusKey])

  const activeTab: BarControl = s.tab === 'roster' ? 'view:roster' : 'view:memorial'
  return (
    <div className="sui fl-screen cp-screen" data-testid="captains-screen">
      <header className="sui-head">
        <span className="fl-count" data-testid="captains-summary">
          {summaryLine(s.rows, s.hiredCap, s.soleTrader)}
        </span>
      </header>
      <div className="fl-bar" role="toolbar" aria-label="Captains and memorial">
        {BAR.map((c, k) => (
          <button
            key={c}
            type="button"
            tabIndex={-1}
            className={`sui-btn fl-ctl fl-ctl--${c.replace(':', '-')}`}
            data-testid={`captains-${c.replace(':', '-')}`}
            aria-pressed={c === activeTab ? true : undefined}
            {...focusAttr(s.picker === null && s.focus.zone === 'bar' && s.focus.index === k)}
            onClick={() => p.dispatch({ type: 'press', control: c })}
          >
            {BAR_LABEL[c]}
            {c === 'view:memorial' && s.memorial.length > 0 ? ` (${s.memorial.length})` : ''}
          </button>
        ))}
      </div>
      <p className="fl-notice" role="status" aria-live="polite" data-testid="captains-notice">
        {s.notice ?? (p.loading === true ? 'Reading your captains…' : '')}
      </p>
      <div className="cp-list" ref={listRef}>
        {s.tab === 'memorial' ? (
          <Memorial rows={s.memorial} focused={s.focus.zone === 'list' ? s.focus.index : -1} empty={p.config.memorialEmpty} />
        ) : s.rows.length === 0 ? (
          <p className="fl-empty">{p.loading === true ? '' : p.config.rosterEmpty}</p>
        ) : (
          <ul className="cp-rows" aria-label="Captains">
            {s.rows.map((r, i) => (
              <CaptainItem key={r.id} s={s} r={r} i={i} dispatch={p.dispatch} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
